import { createAction, props } from '@ngrx/store';

import { DatablockListEntity } from '../components/datablock-list/datablock-list.entity';

interface IPersistenceInfo {
	isPersisted: boolean;
	partitionCount: number;
	persistenceDate: Date | null;
	persistenceDuration: number;
	persistenceSize: number;
}

export enum DatablockActions {
	InitDatablockList = '[Datablock] get datablock list',
	RefreshDatablockList = '[BE][Datablock] refresh datablock list',
	FetchDatablockListFail = '[BE][Datablock] fetch datablock list fail',
	FetchDatablockListSuccess = '[BE][Datablock] fetch datablock list success',
	ConstructDatablockList = '[Datablock] Construct datablock list',
	ConstructDatablockListSuccess = '[BE][Datablock] Construct datablock list success',
	ConstructDatablockListFail = '[BE][Datablock] Construct datablock list fail',
	NavigateToDatablockCreateRoute = '[Datablock] navigate to datablock create route',
	NavigateToDatablockRoute = '[Datablock] navigate to datablock route',
	NavigateToDatablockDuplicateRoute = '[Datablock] navigate to datablock duplicate route',
	NavigateToCreateFromDatablockRoute = '[Datablock] navigate to create from datablock route',
	NavigateToExportDatablockRoute = '[Datablock] navigate to export datablock route',
	UpdateIncludePublic = '[Datablock] update include public',
	OpenDeleteConfirmationPopup = '[Datablock] open delete confirm popup',
	DeleteDatablock = '[Datablock] delete datablock',
	DeleteDatablockFail = '[BE][Datablock] delete datablock fail',
	OpenPersistConfirmationPopup = '[Datablock] open persist datablock popup',
	PersistDatablock = '[Datablock] persist datablock',
	PersistDatablockStarted = '[Datablock] persist datablock started',
	PersistDatablockStopped = '[Datablock] persist datablock stopped',
	PersistDatablockFail = '[BE][Datablock] persist datablock fail',
	OpenUnpersistConfirmationPopup = '[Datablock] open unpersist datablock popup',
	UnpersistDatablock = '[Datablock] unpersist datablock',
	UnpersistDatablockStarted = '[Datablock] unpersist datablock started',
	UnpersistDatablockStopped = '[Datablock] unpersist datablock stopped',
	UnpersistDatablockFail = '[BE][Datablock] unpersist datablock fail',
	OpenRepersistConfirmationPopup = '[Datablock] open repersist datablock popup',
	RepersistDatablock = '[Datablock] repersist datablock',
	RepersistDatablockStarted = '[Datablock] repersist datablock started',
	RepersistDatablockStopped = '[Datablock] repersist datablock stopped',
	RepersistDatablockFail = '[BE][Datablock] repersist datablock fail',
	BulkDelete = '[Datablock] delete many datablocks',
	PersistanceActionFinish = '[Datablock] persistence action finished',
	TrackDatablockAction = '[BE][Datablock] track datablock action',
	ToggleDatablockStatus = '[Datablock] toggle datablock status',
	StopPersistence = '[Datablock] stop persistence',
	StopPersistenceFail = '[Datablock] stop persistence fail',
}

export const initDatablockList = createAction(
	DatablockActions.InitDatablockList
);

export const refreshDatablockList = createAction(
	DatablockActions.RefreshDatablockList
);

export const fetchDatablockListSuccess = createAction(
	DatablockActions.FetchDatablockListSuccess,
	props<{ datablockList: DatablockListEntity }>()
);

export const fetchDatablockListFail = createAction(
	DatablockActions.FetchDatablockListFail,
	props<{ error: unknown }>()
);

export const constructDatablockList = createAction(
	DatablockActions.ConstructDatablockList
);

export const constructDatablockListSuccess = createAction(
	DatablockActions.ConstructDatablockListSuccess,
	props<{ datablockList: DatablockListEntity }>()
);

export const constructDatablockListFail = createAction(
	DatablockActions.ConstructDatablockListFail,
	props<{ error: unknown }>()
);

export const navigateToDatablock = createAction(
	DatablockActions.NavigateToDatablockRoute,
	props<{ id: number }>()
);
export const navigateToDatablockDuplicate = createAction(
	DatablockActions.NavigateToDatablockDuplicateRoute,
	props<{ id: number }>()
);
export const navigateToDatablockCreate = createAction(
	DatablockActions.NavigateToDatablockCreateRoute
);
export const navigateToCreateFromDatablock = createAction(
	DatablockActions.NavigateToCreateFromDatablockRoute,
	props<{ id: number }>()
);

export const navigateToExportDatablock = createAction(
	DatablockActions.NavigateToExportDatablockRoute,
	props<{ id: number; label: string }>()
);

export const updateIncludePublic = createAction(
	DatablockActions.UpdateIncludePublic,
	props<{ includePublic: boolean }>()
);

export const openDeleteConfirmationPopup = createAction(
	DatablockActions.OpenDeleteConfirmationPopup,
	props<{ datablockId: Array<number>; datablockLabel: string }>()
);
export const deleteDatablock = createAction(
	DatablockActions.DeleteDatablock,
	props<{ datablockId: number }>()
);

export const bulkDelete = createAction(
	DatablockActions.BulkDelete,
	props<{ datablockId: Array<number> }>()
);

export const deleteDatablockFail = createAction(
	DatablockActions.DeleteDatablockFail
);

export const openPersistConfirmationPopup = createAction(
	DatablockActions.OpenPersistConfirmationPopup,
	props<{
		datablockId: Array<number>;
		datablockCode: Array<string>;
		datablockLabel: string;
	}>()
);

export const openUnpersistConfirmationPopup = createAction(
	DatablockActions.OpenUnpersistConfirmationPopup,
	props<{
		datablockId: Array<number>;
		datablockCode: Array<string>;
		datablockLabel: string;
	}>()
);

export const openRepersistConfirmationPopup = createAction(
	DatablockActions.OpenRepersistConfirmationPopup,
	props<{
		datablockId: number;
		datablockCode: string;
		datablockLabel: string;
	}>()
);

export const persistDatablock = createAction(
	DatablockActions.PersistDatablock,
	props<{
		datablockId: number;
		datablockCode: string;
		datablockLabel: string;
	}>()
);

export const persistDatablockStarted = createAction(
	DatablockActions.PersistDatablockStarted,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const persistDatablockStopped = createAction(
	DatablockActions.PersistDatablockStopped,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const unpersistDatablock = createAction(
	DatablockActions.UnpersistDatablock,
	props<{
		datablockId: number;
		datablockCode: string;
		datablockLabel: string;
	}>()
);

export const unpersistDatablockStarted = createAction(
	DatablockActions.UnpersistDatablockStarted,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const unpersistDatablockStopped = createAction(
	DatablockActions.UnpersistDatablockStopped,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const repersistDatablock = createAction(
	DatablockActions.RepersistDatablock,
	props<{
		datablockId: number;
		datablockCode: string;
		datablockLabel: string;
	}>()
);

export const repersistDatablockStarted = createAction(
	DatablockActions.RepersistDatablockStarted,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const repersistDatablockStopped = createAction(
	DatablockActions.RepersistDatablockStopped,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const persistenceActionFinish = createAction(
	DatablockActions.PersistanceActionFinish,
	props<{
		datablockId: number;
		persistenceInfo: IPersistenceInfo;
	}>()
);

export const trackAction = createAction(
	DatablockActions.TrackDatablockAction,
	props<{
		datablockId: number;
		jobId: string;
	}>()
);

export const repersistDatablockFail = createAction(
	DatablockActions.RepersistDatablockFail,
	props<{ error: unknown }>()
);
export const unpersistDatablockFail = createAction(
	DatablockActions.UnpersistDatablockFail,
	props<{ error: unknown }>()
);
export const persistDatablockFail = createAction(
	DatablockActions.PersistDatablockFail,
	props<{ error: unknown }>()
);

export const toggleDatablockStatus = createAction(
	DatablockActions.ToggleDatablockStatus,
	props<{ datablockId: number }>()
);

export const stopPersistence = createAction(
	DatablockActions.StopPersistence,
	props<{ jobKey: string }>()
);

export const stopPersistenceFail = createAction(
	DatablockActions.StopPersistenceFail,
	props<{ error: unknown }>()
);
